import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { isPlanConfigVisibleForCommentFilter, withCommentFilterPlan } from '@constants'
import { DrawControllerWrapper, OrthoSnapIndicator } from '@scene'
import { Vector3 } from 'three'
import { Delete, Edit, Height, OpenWith } from '@mui/icons-material'
import useGetPlanSettings from '@editor/hooks/requests/useGetPlanSettings'
import usePlanImageCardEditMode from '@editor/hooks/usePlanImageCardEditMode'
import usePlanImageCardMutations from '@editor/hooks/usePlanImageCardMutations'
import { ADD_PLAN_IMAGE_CARDS_SELECTION_KEY } from '@editor/hooks/usePlanImageCardTool'
import { getPlanViewStorey } from '@editor/pages/PlanView/utils'
import useStructuralPlanningSelection from '@editor/pages/StructuralPlanning/hooks/useStructuralPlanningSelection'
import { TransientDrawState, useCameraStore } from '@modugen/ui-toolkit'
import {
  getSelectedPlanElement,
  useCommentFilterStore,
  useControlStore,
  useModelStore,
  usePlanImageCardStore,
} from '@editorStores'
import {
  usePlanElementSelection,
  useClickActionEnabled,
  useMouseMovedSinceMouseDown,
  useSelectedPlanElementId,
  useSelectionMode,
} from '@editorHooks'
import { DEFAULT_PLAN_IMAGE_CARD_CONFIG } from 'src/domain/planImageCards/defaults'
import { computePlanImageCardLayoutDimensions } from 'src/domain/planImageCards/layout'
import { usePersistentParams } from 'src/hooks'
import SceneHud, { SceneHudAction } from '../SceneHud'
import useCommentSnapTargets from '../hooks/useCommentSnapTargets'
import usePlanPositionSnapTargets from '../hooks/usePlanPositionSnapTargets'
import useScenePositionDrag, { ScenePosition } from '../hooks/useScenePositionDrag'
import PlanImageCardEditPopup from './PlanImageCardEditPopup'
import PlanImageCardPreview from './PlanImageCardPreview'
import ScenePointerTracker from './ScenePointerTracker'
import { isEditableTextTarget } from './utils'

const PlanImageCardController = (): ReactElement => {
  const { pathname } = useLocation()
  const isPasteEnabledView =
    pathname.includes('/structural-planning') || pathname.includes('/results')
  const isVisible = usePlanImageCardStore(state => state.isVisible)
  const cards = usePlanImageCardStore(state => state.cards)
  const selectedId = useSelectedPlanElementId('image-card')
  const { clearPlanSelection } = usePlanElementSelection()
  const placeholderCardIds = usePlanImageCardStore(state => state.placeholderCardIds)
  const imageAspectRatios = usePlanImageCardStore(state => state.imageAspectRatios)
  const selectedPlan = useCommentFilterStore(state => state.selectedPlan)
  const visibleCards = useMemo(
    () =>
      cards.filter(
        card =>
          // The selected card is always shown, regardless of the comment filter.
          card.id === selectedId ||
          isPlanConfigVisibleForCommentFilter(card.planConfig, selectedPlan),
      ),
    [cards, selectedId, selectedPlan],
  )
  const floorplanStorey = useControlStore(state => state.floorplanState.storey)
  const storeyBoundaries = useModelStore(state => state.model.storey_boundaries)
  const visibleStoreys = useModelStore(state => state.visibleStoreys)
  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)
  const snapToAngles = useControlStore(state => state.snapToAngles)
  const snapOrthogonal = useControlStore(state => state.snapOrthogonal)
  const snapToComments = useControlStore(state => state.snapToComments)
  const setIsDrawingActive = useControlStore(state => state.setIsDrawingActive)
  const isOrthographic = useCameraStore(state => state.isOrthographic)
  const clickActionEnabled = useClickActionEnabled()
  const { selectionModeKey, unsetSelectionMode } = useSelectionMode()
  const {
    createPlaceholderPlanImageCardAtPosition,
    createPlanImageCardAtPosition,
    updatePlanImageCardFields,
    deletePlanImageCard,
  } = usePlanImageCardMutations()
  const { enterPlanImageCardEdit, selectPlanImageCardInScene, exitPlanImageCardEdit } =
    usePlanImageCardEditMode()
  const { mouseMoved } = useMouseMovedSinceMouseDown()
  const { projectId } = usePersistentParams() as { projectId?: string }
  const { data: planSettings } = useGetPlanSettings()
  const planScale = planSettings?.scale ?? 100
  const {
    params: { storey: activeStoreyParam },
  } = useStructuralPlanningSelection()

  const [isEditing, setIsEditing] = useState(false)

  const isAddMode = selectionModeKey === ADD_PLAN_IMAGE_CARDS_SELECTION_KEY

  const activeStorey = floorplanStorey ?? activeStoreyParam ?? null
  const is2DMode = activeStorey !== null

  const commentCategorySnapTargets = useCommentSnapTargets(is2DMode)
  const additionalSnapTargets = useMemo(
    () => (snapToComments ? commentCategorySnapTargets : []),
    [snapToComments, commentCategorySnapTargets],
  )

  const { pointTargets: xySnapTargets, lineTargets: xyLineSnapTargets } =
    usePlanPositionSnapTargets(selectedId, {
      snapToCornersAndEdges,
      orthoSnap: snapOrthogonal,
    })

  const commitPosition = useCallback(
    (id: string, position: ScenePosition) => updatePlanImageCardFields(id, { position }),
    [updatePlanImageCardFields],
  )

  const { startDrag, dragPreview, activeDragMode, isDraggingRef } = useScenePositionDrag({
    storeyBoundaries,
    setIsDrawingActive,
    onCommit: commitPosition,
    xySnapTargets,
    xyLineSnapTargets,
  })

  const selectedCard = useMemo(
    () => visibleCards.find(card => card.id === selectedId) ?? null,
    [visibleCards, selectedId],
  )

  // Follow the live drag position while moving the selected card.
  const displayedSelectedCard = useMemo(() => {
    if (!selectedCard) return null
    if (dragPreview && dragPreview.id === selectedCard.id)
      return { ...selectedCard, position: dragPreview.position }
    return selectedCard
  }, [selectedCard, dragPreview])

  // The card's real height needs the loaded image aspect ratio (reported by the
  // preview into the store), so the HUD anchors at the actual bottom edge.
  const selectedCardDims = useMemo(
    () =>
      displayedSelectedCard
        ? computePlanImageCardLayoutDimensions(displayedSelectedCard, {
            planScale,
            imageAspectRatio: imageAspectRatios[displayedSelectedCard.id] ?? null,
          })
        : null,
    [displayedSelectedCard, planScale, imageAspectRatios],
  )

  // Anchor the HUD below the card's bottom edge (the card's y is its top),
  // centred horizontally — mirrors the comment HUD.
  const hudPosition = useMemo(() => {
    if (!displayedSelectedCard || !selectedCardDims) return null
    const { x, y, z } = displayedSelectedCard.position
    return new Vector3(x + selectedCardDims.width / 2, y - selectedCardDims.totalHeight, z)
  }, [displayedSelectedCard, selectedCardDims])

  // Anchor the edit popup at the card's top-right corner.
  const popupAnchor = useMemo(() => {
    if (!displayedSelectedCard || !selectedCardDims) return null
    const { x, y, z } = displayedSelectedCard.position
    return new Vector3(x + selectedCardDims.width, y, z)
  }, [displayedSelectedCard, selectedCardDims])

  const handleDelete = useCallback(
    (id: string) => {
      setIsEditing(false)
      deletePlanImageCard(id)
      exitPlanImageCardEdit()
    },
    [deletePlanImageCard, exitPlanImageCardEdit],
  )

  // Clicking the scene away from the selected card deselects it (see the plan
  // table controller for why the HUD-button / mouse-moved / drag / id cases are
  // skipped).
  const handlePointerMissed = useCallback(
    (event: MouseEvent, cardId: string) => {
      // Only a left click deselects; a right-button camera drag keeps the selection.
      if (event.button !== 0) return
      if (isDraggingRef.current || mouseMoved.current) return
      const selectedPlanElement = getSelectedPlanElement()
      if (selectedPlanElement?.type !== 'image-card' || selectedPlanElement.id !== cardId) return
      const target = event.target
      if (target instanceof Element && target.closest('.scene-hud-button')) return
      clearPlanSelection()
    },
    [isDraggingRef, mouseMoved, clearPlanSelection],
  )

  const hudGroups = useMemo<SceneHudAction[][]>(() => {
    if (!displayedSelectedCard) return []

    const moveActions: SceneHudAction[] = [
      {
        key: 'move-xy',
        tooltip: 'In der Ebene verschieben',
        icon: <OpenWith fontSize="small" />,
        onPointerDown: event =>
          startDrag('xy', displayedSelectedCard.id, displayedSelectedCard.position, event),
        active: activeDragMode === 'xy',
        dataCy: 'plan-image-card-move-xy',
      },
    ]
    if (!isOrthographic) {
      moveActions.push({
        key: 'move-z',
        tooltip: 'Vertikal verschieben (z)',
        icon: <Height fontSize="small" />,
        onPointerDown: event =>
          startDrag('z', displayedSelectedCard.id, displayedSelectedCard.position, event),
        active: activeDragMode === 'z',
        dataCy: 'plan-image-card-move-vertical',
      })
    }

    return [
      [
        ...moveActions,
        {
          key: 'edit',
          tooltip: 'Plan-Bild bearbeiten',
          icon: <Edit fontSize="small" />,
          onClick: () => setIsEditing(true),
          active: isEditing,
          dataCy: 'plan-image-card-edit',
        },
      ],
      [
        {
          key: 'delete',
          tooltip: 'Plan-Bild löschen',
          icon: <Delete fontSize="small" />,
          onClick: () => handleDelete(displayedSelectedCard.id),
          variant: 'danger',
          dataCy: 'plan-image-card-delete',
        },
      ],
    ]
  }, [displayedSelectedCard, activeDragMode, isOrthographic, isEditing, startDrag, handleDelete])

  const resolveZ = useCallback(
    (point: Vector3) => {
      if (is2DMode && activeStorey) {
        const boundary = storeyBoundaries[activeStorey]
        if (boundary) return boundary[1]
      }
      return point.z
    },
    [activeStorey, is2DMode, storeyBoundaries],
  )

  // Flags a new card for the filtered plan, or that filter (e.g. the plan view's) hides it.
  const newCardPlanConfig = useMemo(
    () =>
      withCommentFilterPlan(
        { ...DEFAULT_PLAN_IMAGE_CARD_CONFIG },
        selectedPlan,
        activeStorey ? getPlanViewStorey(activeStorey, visibleStoreys) : null,
      ),
    [activeStorey, selectedPlan, visibleStoreys],
  )

  const handlePick = useCallback(
    async (point: Vector3) => {
      const z = resolveZ(point)
      const created = await createPlaceholderPlanImageCardAtPosition(
        { x: point.x, y: point.y, z },
        newCardPlanConfig,
      )

      if (created) {
        enterPlanImageCardEdit(created.id)
      }
      unsetSelectionMode()
    },
    [
      createPlaceholderPlanImageCardAtPosition,
      enterPlanImageCardEdit,
      newCardPlanConfig,
      resolveZ,
      unsetSelectionMode,
    ],
  )

  const handlePasteImage = useCallback(
    async (file: File) => {
      const pointer = usePlanImageCardStore.getState().lastScenePointer
      const base = pointer ?? { x: 0, y: 0, z: 0 }
      const boundary = activeStorey ? storeyBoundaries[activeStorey] : undefined
      const z = boundary ? boundary[1] : base.z
      const created = await createPlanImageCardAtPosition(
        { x: base.x, y: base.y, z },
        { file, planConfig: newCardPlanConfig },
      )
      if (created) {
        enterPlanImageCardEdit(created.id)
      }
    },
    [
      activeStorey,
      createPlanImageCardAtPosition,
      enterPlanImageCardEdit,
      newCardPlanConfig,
      storeyBoundaries,
    ],
  )

  // Pasting an image creates a new plan image card on structural planning and
  // results. Non-image clipboard content is ignored so text pastes elsewhere
  // (forms, tables, etc.) are not hijacked. Pastes while a text field is
  // focused always belong to that field, even if they contain an image.
  useEffect(() => {
    if (!isPasteEnabledView) return

    const handlePaste = (event: ClipboardEvent) => {
      if (isEditableTextTarget(event.target) || isEditableTextTarget(document.activeElement)) {
        return
      }

      const items = event.clipboardData?.items
      if (!items) return

      for (const item of Array.from(items)) {
        if (!item.type.startsWith('image/')) continue
        const file = item.getAsFile()
        if (!file) continue
        event.preventDefault()
        handlePasteImage(file)
        break
      }
    }

    window.addEventListener('paste', handlePaste)
    return () => window.removeEventListener('paste', handlePaste)
  }, [handlePasteImage, isPasteEnabledView])

  return (
    <>
      <ScenePointerTracker enabled resolveZ={resolveZ} />

      {isAddMode && (
        <DrawControllerWrapper
          enabled={clickActionEnabled}
          enableIndicator
          xyOnly={is2DMode}
          onDrawEnd={(state: TransientDrawState) => {
            if (state.drawPoint) handlePick(state.drawPoint.v)
          }}
          additionalSnapTargets={additionalSnapTargets}
          snapToCornersAndEdges={snapToCornersAndEdges}
          snapToAngles={snapToAngles}
          orthoSnap={snapOrthogonal}
        />
      )}

      {isVisible &&
        visibleCards.map(card => {
          const display =
            dragPreview && dragPreview.id === card.id
              ? { ...card, position: dragPreview.position }
              : card
          return (
            <PlanImageCardPreview
              key={card.id}
              planImageCard={display}
              selected={selectedId === card.id}
              isPlaceholder={placeholderCardIds.includes(card.id)}
              projectId={projectId ?? ''}
              isDragging={dragPreview?.id === card.id}
              onClick={() => selectPlanImageCardInScene(card.id)}
              onPointerMissed={
                selectedId === card.id && !isEditing
                  ? event => handlePointerMissed(event, card.id)
                  : undefined
              }
            />
          )
        })}

      {isVisible && !isEditing && displayedSelectedCard && hudPosition && (
        <SceneHud position={hudPosition} groups={hudGroups} />
      )}

      {isVisible && isEditing && selectedCard && popupAnchor && (
        <PlanImageCardEditPopup
          planImageCard={selectedCard}
          anchor={popupAnchor}
          onClose={() => setIsEditing(false)}
        />
      )}

      {dragPreview?.isOrthoSnap && <OrthoSnapIndicator point={dragPreview.position} />}
    </>
  )
}

export default PlanImageCardController
